import * as logger from "common/logger";
import * as numbers from "common/numbers";
import * as layerSupport from "domain/layerSupport";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import * as analyticsService from "services/analyticsService";
import * as staticDataService from "services/staticDataService";
import * as peersSelector from "store/selectors/peers/peersSelectors";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";
import * as utils from "utils";

const createBaseLayer = (layer, peers) => {
  return {
    title: layerSupport.shortLayerName(layer.terms),
    data: [
      {
        label: `${layer.insured} - ${layerSupport.shortLayerName(layer.terms)}`,
        value: layer.premium,
        isPeer: false,
      },
      ...peers.map((peer) => ({
        label: `${peer.insured} - ${layerSupport.shortLayerName(peer.terms)}`,
        value: 0,
        isPeer: true,
      })),
    ],
    chartMaximum: layer.premium,
  };
};

export const calculateChartMaximum = (values, average, premium) => {
  const validValues = [...(values || []), average, premium].filter(
    (value) => !Number.isNaN(value)
  );
  return numbers.roundUpNice(Math.max(...validValues));
};

const blendBaseAndValue = (base, values, average) => {
  return values == null
    ? base
    : {
        ...base,
        chartMaximum: calculateChartMaximum(values, average, base.chartMaximum),
        average,
        data: [
          base.data[0],
          ...utils.zip(base.data.slice(1), values).map(([row, value]) => ({
            ...row,
            value,
          })),
        ],
      };
};

export const makeCharts = ({ data, peerPricingRequest }) => {
  try {
    const bases = (peerPricingRequest?.terms ?? []).map((layer) =>
      createBaseLayer(layer, peerPricingRequest.peers)
    );
    const layers = data?.layers ?? [];

    if (bases.length !== layers.length) {
      return {};
    }

    return {
      group: utils.zip(bases, layers).map(([base, layer]) => {
        return blendBaseAndValue(
          base,
          layer?.adjustedPremium ?? [],
          layer?.impliedPremium ?? null
        );
      }),
      exposure: utils
        .zip(bases, layers)
        .map(([base, layer]) =>
          blendBaseAndValue(
            base,
            layer?.exposureAdjustedPremium ?? [],
            layer?.exposureImpliedPremium ?? null
          )
        ),
    };
  } catch (e) {
    logger.exception(e);
    return {};
  }
};

export const usePeerPricing = () => {
  const { data: analyticsConfig } = staticDataService.useConfigQuery(
    "analytics"
  );

  const persistedState = useSelector(persistenceSelectors.selectPersistedState);
  const peerPricingRequest = useSelector(peersSelector.getPeerPricingRequest);
  const exposureMetric = useSelector(peersSelector.peerExposure);

  const submission = useMemo(() => ({ state: persistedState }), [
    persistedState,
  ]);

  const { data, isFetching } = analyticsService.usePeerAnalyticsQuery(
    {
      engine: analyticsConfig?.peerPricing?.engine,
      exposure: exposureMetric?.key,
      submission,
    },
    {
      skip: !analyticsConfig?.peerPricing?.engine,
    }
  );

  const charts = makeCharts({
    data,
    peerPricingRequest,
  });

  return {
    charts,
    isFetching,
  };
};
